/**
 * Responsability: allows to handle operation for all navigation menu - exposes helper functions and keeps a current route state to allow reactivity
 * Parameters needed:
 * - menu               # menu to inspect, if any
 * Exposed variables
 * - itemRoute          # For a given item, return the internal routing path if any
 * - isActive           # For a given item, return whether it is active - in case of main menu, returns true if any of the subitems is active
 * - hasSubmenu         # For a given item, returns whether or not the item is a routing element or a menu that holds subitems
 * - Main tabs specific:
 * - updateCurrentTab   # Function to update the currently active tab index for the given menu
 *
 */

/* UTILS */
import { computed, type Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

/* TYPES */
import type { NavItemType } from '@/types/navigation/navigation.types';

export function useNavMenu(menu: null | Ref<NavItemType[]> = null) {
  const route = useRoute();

  const currentRoute = ref('');

  const currentTabIndex = ref();

  let currentDropdown: string | undefined = '';

  /**
   * route for a given item
   * @param {NavItemType} item - item to evaluate
   */
  const itemRoute = computed(() => {
    return (item: NavItemType) => (!hasSubmenu(item) && !item.externalRedirection ? item.route : undefined);
  });

  /**
   *  Is a given item the currently active one
   *  @param {NavItemType} item - item to evaluate
   */
  const isActive = computed(() => {
    return item => {
      if (hasSubmenu(item)) {
        return item.items.find(subItem => subItem.route === currentRoute.value);
      } else {
        return item.route === currentRoute.value;
      }
    };
  });

  /**
   * Allows to set the active tab to the current one according to URL
   */
  function setCurrentTab() {
    if (menu) {
      const activeTab = menu.value.findIndex(menuItem => {
        return (
          menuItem.route === currentRoute.value ||
          menuItem?.items?.some(subItem => {
            return subItem.route === currentRoute.value;
          })
        );
      });
      currentTabIndex.value = activeTab;
    }
  }

  /**
   * Function called when a menu dropdown is open or closed - allows to reset active tab when no dropdown is open
   * @param {Boolean} isOpen - is the dropdown open or closed
   * @param {string} dropdown - label of updated dropdown
   */
  function updateCurrentTab(isOpen, dropdown) {
    if (isOpen) {
      currentDropdown = dropdown;
    } else if (currentDropdown === dropdown) {
      currentDropdown = undefined;
    }
    if (!currentDropdown) {
      setCurrentTab();
    }
  }
  /**
   * Allows to know if given menu item has
   * @param {NavItemType} item item to evaluate
   * @returns {Boolean} does the item have a submenu
   */
  function hasSubmenu(item: NavItemType) {
    return !item.route && !item.externalRedirection;
  }

  /** WATCHERS */
  // On route change, update currently active tab
  watch(
    route,
    updatedRoute => {
      currentRoute.value = updatedRoute.path;
      setCurrentTab();
    },
    { immediate: true }
  );

  return { currentRoute, currentTabIndex, updateCurrentTab, hasSubmenu, itemRoute, isActive };
}
